import { useEffect, useRef, useState } from 'react';

import type { BedienerKurz } from '../api';
import { FeldScanErkennung, ladeScannerPraefix, PRAEFIX_ABSTAND_MS } from '../scanner';

/** Zeitpunkt einer Taste: Zeitstempel des Ereignisses, sonst jetzt (wie im Verkauf). */
function tastenZeit(zeitstempel: number): number {
  const jetzt = performance.now();
  return zeitstempel > 0 && zeitstempel <= jetzt + 1 ? zeitstempel : jetzt;
}

/** Scannerpräfix des Geräts; ohne lesbaren Speicher keins. */
function praefix(): string | null {
  try {
    return ladeScannerPraefix(localStorage);
  } catch {
    return null;
  }
}

/**
 * Bediener anmelden: Name antippen; ist eine PIN gesetzt, folgt ein
 * Ziffernblock. Die PIN geht nur an den Server und wird nicht gespeichert.
 */
export function BedienerAnmeldung(props: {
  bediener: BedienerKurz[];
  laeuft: boolean;
  fehler: string | null;
  onAnmelden: (b: BedienerKurz, pin: string | undefined) => void;
  /** Ohne: kein Abbrechen (Anmeldeseite vor dem Verkauf). */
  onAbbrechen?: (() => void) | undefined;
  /** Als ganze Seite statt als Dialog über dem Verkauf. */
  seite?: boolean;
  /** Hinweis über der Auswahl (z. B. „Bediener werden geladen …“). */
  hinweis?: string | undefined;
}) {
  const [gewaehlt, setGewaehlt] = useState<BedienerKurz | null>(null);
  const [pin, setPin] = useState('');
  /**
   * W5-A4: Ein Scan ins PIN-Feld ist nie eine PIN – sonst löste das Enter des Scanners einen
   * Anmeldeversuch mit den Barcode-Ziffern aus (fünf Fehlversuche sperren den Bediener). Das
   * Feld erkennt Scans selbst (im Verkauf wie am Handterminal, das keinen Scan-Listener hat):
   * schnelle Folge ab 4 Zeichen bzw. nach dem Scannerpräfix → verwerfen. Eine von Hand getippte
   * PIN bleibt gültig, auch wenn nur die letzte Ziffer und Enter schnell aufeinander folgen.
   */
  const scan = useRef(new FeldScanErkennung());
  const praefixTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (praefixTimer.current !== null) {
        clearTimeout(praefixTimer.current);
      }
    },
    [],
  );
  /** Präfix-Scan ohne Abschluss: nach der Frist verwerfen (Feld zurück), wie im Verkauf. */
  function praefixUeberwachen(): void {
    if (praefixTimer.current !== null) {
      clearTimeout(praefixTimer.current);
      praefixTimer.current = null;
    }
    if (!scan.current.praefixLaeuft()) {
      return;
    }
    praefixTimer.current = setTimeout(() => {
      praefixTimer.current = null;
      const v = scan.current.ablauf(performance.now());
      if (v !== null) {
        setPin(v.wertVorher);
      } else {
        praefixUeberwachen();
      }
    }, PRAEFIX_ABSTAND_MS + 20);
  }

  function absenden(): void {
    // nie während eines laufenden Präfix-Scans (auch nicht über „OK“): die Ziffern im Feld
    // stammen dann vom Scanner
    if (gewaehlt === null || props.laeuft || scan.current.praefixLaeuft()) {
      return;
    }
    props.onAnmelden(gewaehlt, gewaehlt.pinNoetig ? pin : undefined);
    setPin('');
  }

  return (
    <div
      className={props.seite === true ? 'anmelde-seite' : 'zahlung-dialog'}
      role={props.seite === true ? 'region' : 'dialog'}
      aria-modal={props.seite === true ? undefined : true}
      aria-label="Bediener anmelden"
    >
      <div className="karte">
        <h2>{gewaehlt === null ? 'Bediener anmelden' : gewaehlt.name}</h2>
        {gewaehlt === null && props.seite === true && (
          <p className="hinweis">
            {props.hinweis ?? 'Zum Kassieren zuerst anmelden – Namen antippen.'}
          </p>
        )}
        {gewaehlt === null ? (
          <div
            className="chips"
            role="group"
            aria-label="Bediener"
            style={{ justifyContent: 'center' }}
          >
            {props.bediener.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => {
                  if (b.pinNoetig) {
                    setGewaehlt(b);
                  } else {
                    props.onAnmelden(b, undefined);
                  }
                }}
              >
                {b.name}
                {b.pinNoetig && ' 🔒'}
              </button>
            ))}
            {props.bediener.length === 0 && (
              <span className="hinweis">Keine aktiven Bediener – im Backoffice anlegen.</span>
            )}
          </div>
        ) : (
          <>
            <label>
              PIN
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                // Scans erkennt das Feld selbst (auch das Präfix); der Scan-Listener des
                // Verkaufs lässt es deshalb aus
                data-eigene-scanerkennung=""
                value={pin}
                maxLength={8}
                onChange={(e) => {
                  setPin(e.target.value.replace(/\D/g, ''));
                }}
                onKeyDown={(e) => {
                  const treffer = scan.current.taste(
                    e.key,
                    tastenZeit(e.timeStamp),
                    pin,
                    praefix(),
                  );
                  praefixUeberwachen();
                  const vonHand =
                    treffer === null ||
                    // letzte Ziffer + Enter schnell hintereinander: nur dann ein möglicher Scan,
                    // wenn das Feld danach keine vollständige PIN (4–8 Ziffern) enthält
                    (typeof treffer === 'object' &&
                      'kurz' in treffer &&
                      treffer.kurz &&
                      !treffer.praefix &&
                      pin.length >= 4);
                  if (vonHand) {
                    if (e.key === 'Enter') {
                      absenden();
                    }
                    return;
                  }
                  // Präfix, Scanfolge, Nachlauf oder Rest eines Scans: nie als PIN, nie als Anmeldung
                  e.preventDefault();
                  if (typeof treffer === 'object') {
                    setPin('verworfen' in treffer ? treffer.wertVorher : '');
                  }
                }}
                style={{ textAlign: 'center', fontSize: '1.6rem', letterSpacing: '0.4rem' }}
              />
            </label>
            <div className="ziffern pin-block" role="group" aria-label="Ziffernblock">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'OK'].map((t) => (
                <button
                  key={t}
                  type="button"
                  className={t === 'OK' ? 'taste' : 'taste zweit'}
                  disabled={t === 'OK' && (pin.length < 4 || props.laeuft)}
                  onClick={() => {
                    if (t === '⌫') {
                      setPin(pin.slice(0, -1));
                    } else if (t === 'OK') {
                      absenden();
                    } else if (pin.length < 8) {
                      setPin(pin + t);
                    }
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </>
        )}
        {props.fehler !== null && <p className="fehler">{props.fehler}</p>}
        <div className="zeile" style={{ justifyContent: 'center' }}>
          {gewaehlt !== null && (
            <button
              type="button"
              className="taste zweit"
              onClick={() => {
                setGewaehlt(null);
                setPin('');
              }}
            >
              Zurück
            </button>
          )}
          {props.onAbbrechen !== undefined && (
            <button type="button" className="taste zweit" onClick={props.onAbbrechen}>
              Abbrechen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
